import React, { useState } from 'react';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { AppActions } from 'src/store/AppReducer';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import validator from 'validator';
import { useForm } from 'react-hook-form';
import { createStakeHolder } from 'src/services/projectService';
import { RootStateType } from 'src/store/RootReducer';
import MuiPhoneNumber from 'material-ui-phone-number';

export interface StakeholderInformation {
  name: string;
  designation: string;
  email: string;
  contact: string;
}

const AddStakeHolder = ({
  toggle,
  open,
  getData,
  length,
  customerId,
  segmentId,
  segmentName,
  customerName,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage,
}) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 2,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const { register, handleSubmit, reset } = useForm<StakeholderInformation>();

  const [nameError, setNameError] = useState('');
  const [designationError, setDesignationError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactNumber, setContactNumber] = useState('');

  const onClose = () => {
    reset();
    setContactNumber('');
    setContactError('');
    toggle();
    onClearErrorMessages();
  };

  //clear inline error messages
  const onClearErrorMessages = ( () => {
    setNameError('');
    setDesignationError('');
    setEmailError('');
    setContactError('');
  })

  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onSubmit = (data: StakeholderInformation) => {
    if (
      data.name !== '' &&
      data.name.length < 31 &&
      data.name.trim().length > 0 &&
      data.designation !== '' &&
      data.designation.length < 31 &&
      data.designation.trim().length > 0 &&
      data.email !== '' &&
      data.email.trim().length > 0 &&
      validator.isEmail(data.email) && 
      contactNumber !== '' &&
      contactNumber.length > 11
    ) {
      dispatch(AppActions.showLoader());

      setNameError('');
      setDesignationError('');
      setEmailError('');
      setContactError('');

      const body = {
        name: data.name,
        designation: data.designation,
        email: data.email,
        contactNumber: contactNumber,
        segmentId: segmentId,
        customerId: customerId
      };

      createStakeHolder(body)
        .then(() => {
          reset();
          setContactNumber('');
          setSnackBarMessage('You have created a stakeholder successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          getData();
          toggle();
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable create a stakeholder');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        });
    } if (data.name === '') {
      setNameError('Stakeholder name is required.');
    } else if (data.name.trim().length < 1) {
      setNameError('Stakeholder name cannot be only spaces');
    } if (data.designation === '') {
      setDesignationError('Designation is required.');
    } else if (data.designation.trim().length < 1) {
      setDesignationError('Designation cannot be only spaces');
    } if (data.email === '') {
      setEmailError('Email is required.');
    } else if (data.email.trim().length < 1) {
      setEmailError('Email cannot be only spaces');
    } if (contactNumber === '') {
      setContactError('Contact number is required.');
    } else if (contactNumber.trim().length < 1) {
      setContactError('Contact cannot be only spaces');
    } else if(contactNumber.trim().length < 12) {
      setContactError('Contact Number must not be less than 10 numbers.');
    }
     else if (!validator.isEmail(data.email)) {
      setEmailError('Invalid email address.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12} sx={{ pt: 3, pb: 1, pl: 2 }}>
        <Typography style={{ fontSize: '15px' }} variant="h3">
          Create New Stakeholder
        </Typography>
          <Typography style={{ fontSize: '14px', marginTop: 5 }}>
            {segmentName} : {customerName}
          </Typography>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Stakeholder Name<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            id="stakeholderName"
            name="stakeholderName"
            inputProps={{ maxLength: 30 }}
            size="small"
            {...register('name', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Stakeholder Name"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length > 30) {
                setNameError('Stakeholder name must not exceed 30 characters');
              } else {
                setNameError('');
              }
            }}
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {nameError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Designation<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            id="stakeholderDesignation"
            name="stakeholderDesignation"
            size="small"
            inputProps={{ maxLength: 30 }}
            {...register('designation', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Designation"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length > 30) {
                setDesignationError(
                  'Designation must not exceed 30 characters'
                );
              } else {
                setDesignationError('');
              }
            }}
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {designationError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Email<span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            id="stakeholderEmail"
            name="stakeholderEmail"
            size="small"
            {...register('email', {})}
            style={TextFieldStyle}
            fullWidth
            placeholder="Enter Email"
            variant="outlined"
            autoComplete="new-password"
/*            onChange={(e) => {
              if (e.target.value.length > 20) {
                setEmailError('Email must not exceed 20 characters');
              } else {
                setEmailError('');
              }
            }}*/
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {emailError}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Contact Number<span style={{ color: 'red' }}>*</span>
          </Typography>
          <MuiPhoneNumber
            id="stakeholderPhoneNumber"
            defaultCountry={'lk'}
            size="small"
            {...register('contact', {})}
            InputProps={{
              style: { fontSize: '12px', height: '2.25rem' }
            }}
            style={TextFieldStyle}
            fullWidth
            type="text"
            placeholder="Enter Contact Number"
            variant="outlined"
            value={contactNumber}
            onChange={(e) => {
              setContactNumber(e);
              if (e.length > 11) {
                setContactError('');
              }
            }}
          />
          <Typography
            style={{
              paddingLeft: 15,
              paddingRight: 15,
              fontSize: '11px'
            }}
            color="red"
          >
            {contactError}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            mb: 2,
            mt: 2,
            pr: 2,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          xs={12}
        >
          <Button
            id="stakeholder-btn-onClose"
            sx={{
              width: '10%',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '13px'
            }}
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            id="stakeholder-btn-onSubmit"
            disabled={isLoading}
            className={isLoading ? 'loading' : ''}
            sx={{
              ml: 2,
              width: '10%',
              fontSize: '13px',
              fontWeight: 'normal',
              background: '#0069E4'
            }}
            variant="contained"
            type="submit"
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AddStakeHolder;
